import useBuilderService from "../composables/builderIoService";
import type { Ref } from "vue";
import { ref, watch } from "vue";
import { CONTENT_TYPES, TAGS_ARRAY } from '../../shared/utils/contentTypes'
import { URL_CONFIG } from '../../shared/utils/url.const.js'
import { extractTextFromHtml } from "#shared/utils/common";

export const useTake5DailyDetails = (slug: string) => {
  const builder = useBuilderService();

  const { data, error, pending } = useAsyncData(`take5daily-${slug}`, () =>
    builder.fetchTake5DailyDetails(slug)
  );

  return {
    blogDetails: data,
    error,
    pending
  };
};

export const useMorePodcast = (
  tags: Ref<string[]>,
  currentId: Ref<number | null>
) => {
  const builder = useBuilderService();
  const morePodcast = ref<any[]>([]);
  const error = ref<unknown>(null);
  const loading = ref(true);

  const tagsArray = TAGS_ARRAY;

  const mountTimestamp = ref(Date.now());

  const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      // NOSONAR: Safe usage of Math.random for UI only
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i]!, shuffled[j]!] = [shuffled[j]!, shuffled[i]!];
    }
    return shuffled;
  };

  const dedupe = (items: any[]) => {
    const seen = new Set();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  const getRandomTag = (currentTags: string[], availablePool: string[]) => {
    const availableTags = availablePool.filter(
      (tag) => !currentTags.includes(tag)
    );

    if (availableTags.length === 0) {
      return null;
    }

    // NOSONAR: Safe usage of Math.random for UI only
    const randomIndex = Math.floor(Math.random() * availableTags.length);
    return availableTags[randomIndex];
  };

  const combineTagsWithRandom = (currentTags: string[], shuffledTags: string[]) => {
    if (!currentTags || currentTags.length === 0) {
      return shuffledTags;
    }

    const randomTag = getRandomTag(currentTags, shuffledTags);

    if (!randomTag) {
      return currentTags;
    }

    return [...currentTags, randomTag];
  };

  watch(
    [tags, currentId, mountTimestamp],
    async ([newTags, newId, timestamp]) => {
      loading.value = true;

      try {
        if (!newId) {
          morePodcast.value = [];
          loading.value = false;
          return;
        }

        const shuffledTagsArray = shuffleArray(tagsArray);

        const combinedTags = combineTagsWithRandom(newTags, shuffledTagsArray);

        let related = await builder.fetchRelatedItems(combinedTags);
        const relatedData = related.filter((item: any) => item?.data?.takeFiveId !== newId)
          .map((item: any) => ({
            ...item.data,
            id: item.id
          }))

        const relatedFormatted = relatedData.map((item: any) =>
          formatMorePodcastData(item)
        );

        if (relatedFormatted.length === 0) {
          morePodcast.value = [];
          loading.value = false;
          return;
        }

        if (relatedFormatted.length >= 3) {
          morePodcast.value = relatedFormatted.slice(0, 3);
          loading.value = false;
          return;
        }

        let allItems = [...relatedFormatted];
        const usedTags = [...combinedTags];
        let attempts = 0;
        const maxAttempts = shuffledTagsArray.length;

        while (allItems.length < 3 && attempts < maxAttempts) {
          const availableTags = shuffledTagsArray.filter(tag => !usedTags.includes(tag));

          if (availableTags.length === 0) {
            break;
          }

          const additionalTag = availableTags[0]!;
          usedTags.push(additionalTag!);

          const additionalCombinedTags = (!newTags || newTags.length === 0)
            ? [additionalTag]
            : [...newTags, additionalTag];

          const additionalRelated = await builder.fetchRelatedItems(additionalCombinedTags);
          const additionalData = additionalRelated.filter(
            (item: any) =>
              item?.data?.takeFiveId !== newId &&
              !allItems.some(
                (existing) => existing.id === item?.data?.takeFiveId
              )
          )
            .map((item: any) => ({
              ...item.data,
              id: item.id
            }))

          const additionalFormatted = additionalData.map((item: any) =>
            formatMorePodcastData(item)
          );

          allItems = dedupe([...allItems, ...additionalFormatted]);
          attempts++;
        }

        morePodcast.value = allItems.slice(0, 3);

      } catch (err) {
        error.value = err;
      } finally {
        loading.value = false;
      }
    },
    { immediate: true }
  );
  return { morePodcast, error, loading };
};

const formatMorePodcastData = (item: any) => {

  const defaultImage = new URL('@/assets/images/default-podcast.png', import.meta.url).href;
  const getImage = () => {
    return (
      item?.media?.medFile ||
      item?.cau?.value?.data?.cauAvatarMedia?.medFile || defaultImage
    );
  };

  const stripHtml = (html: string = ""): string => { return extractTextFromHtml(html) };

  const getExcerpt = () => {
    const d = item;
    return stripHtml(d?.description || d?.metaDescription || "");
  };

  const podcastFormatted: any = {};
  podcastFormatted.title = item?.name || item?.metaTitle || "";
  podcastFormatted.url = `${URL_CONFIG.TAKE_FIVE_PODCAST}/${item?.urlKey || ""}`;
  podcastFormatted.image = getImage();
  podcastFormatted.excerpt = getExcerpt();
  podcastFormatted.publishAt = item?.publishAt || "";
  podcastFormatted.authorName = item?.cau?.value?.data?.cauName || "Unknown Author";
  podcastFormatted.type = CONTENT_TYPES.PODCAST;
  podcastFormatted.audioLink = item?.audioLink || "";
  podcastFormatted.videoLink = item?.videoLink || "";
  podcastFormatted.id = item?.id || "";
  podcastFormatted.takeFiveId = item?.id || "";

  return podcastFormatted;
};
